import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Clock } from "lucide-react";

const VIEWS = ["day", "week", "month"];
const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  "checked-in": "bg-amber-100 text-amber-800 border-amber-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "no-show": "bg-red-100 text-red-800 border-red-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

function startOfWeek(d) { const x = new Date(d); const day = x.getDay(); x.setDate(x.getDate() - day); x.setHours(0,0,0,0); return x; }
function fmtDate(d) { return d.toISOString().slice(0,10); }

export default function Appointments() {
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(new Date());
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", start: "", end: "", mode: "offline", type: "Consultation", notes: "" });

  const { rangeStart, rangeEnd, days } = useMemo(() => {
    const c = new Date(cursor);
    if (view === "day") {
      const s = new Date(c); s.setHours(0,0,0,0);
      const e = new Date(c); e.setHours(23,59,59,999);
      return { rangeStart: s, rangeEnd: e, days: [s] };
    }
    if (view === "week") {
      const s = startOfWeek(c);
      const e = new Date(s); e.setDate(e.getDate() + 6); e.setHours(23,59,59,999);
      const ds = Array.from({length: 7}, (_, i) => { const d = new Date(s); d.setDate(d.getDate() + i); return d; });
      return { rangeStart: s, rangeEnd: e, days: ds };
    }
    // month
    const s = new Date(c.getFullYear(), c.getMonth(), 1);
    const e = new Date(c.getFullYear(), c.getMonth() + 1, 0, 23, 59, 59);
    const start = startOfWeek(s);
    const ds = [];
    for (let i = 0; i < 42; i++) { const d = new Date(start); d.setDate(d.getDate() + i); ds.push(d); }
    return { rangeStart: s, rangeEnd: e, days: ds };
  }, [view, cursor]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/appointments", { params: { start: rangeStart.toISOString(), end: rangeEnd.toISOString() } });
      setItems(data);
    } catch (err) { console.error("appointments load failed:", err?.message); }
  }, [rangeStart, rangeEnd]);

  useEffect(() => {
    load();
    api.get("/clients").then(r => setClients(r.data)).catch(err => console.error("clients load failed:", err?.message));
  }, [load]);

  const create = async () => {
    if (!form.client_id || !form.start) { toast.error("Client and start time required"); return; }
    await api.post("/appointments", form);
    toast.success("Appointment created");
    setOpen(false); load();
  };

  const updateStatus = async (id, status) => { await api.patch(`/appointments/${id}`, { status }); load(); };

  const itemsByDay = useMemo(() => {
    const map = {};
    items.forEach(a => { const k = (a.start || "").slice(0,10); (map[k] = map[k] || []).push(a); });
    return map;
  }, [items]);

  const shift = (dir) => {
    const c = new Date(cursor);
    if (view === "day") c.setDate(c.getDate() + dir);
    else if (view === "week") c.setDate(c.getDate() + dir * 7);
    else c.setMonth(c.getMonth() + dir);
    setCursor(c);
  };

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Calendar</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Appointments</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-full border border-slate-200 dark:border-slate-800 p-1">
            {VIEWS.map(v => (
              <button key={v} onClick={()=>setView(v)} data-testid={`view-${v}`}
                className={`px-4 h-9 rounded-full text-xs uppercase tracking-widest ${view === v ? "bg-[#1E3A8A] text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>{v}</button>
            ))}
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill h-11 px-5" data-testid="new-appointment-btn"><Plus className="w-4 h-4 mr-1.5" /> New</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl">Schedule appointment</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Client</Label>
                  <Select value={form.client_id} onValueChange={v=>setForm({...form,client_id:v})}>
                    <SelectTrigger><SelectValue placeholder="Choose client" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Field label="Start"><Input type="datetime-local" value={form.start} onChange={e=>setForm({...form,start:e.target.value})} /></Field>
                <Field label="End"><Input type="datetime-local" value={form.end} onChange={e=>setForm({...form,end:e.target.value})} /></Field>
                <Field label="Mode">
                  <Select value={form.mode} onValueChange={v=>setForm({...form,mode:v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["online","offline","phone","video"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Type"><Input value={form.type} onChange={e=>setForm({...form,type:e.target.value})} /></Field>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Notes</Label>
                  <Textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={create} className="bg-[#1E3A8A] btn-pill">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => shift(-1)}><ChevronLeft className="w-4 h-4" /></Button>
        <div className="font-display text-xl min-w-[220px] text-center">
          {view === "month" ? cursor.toLocaleString("default", { month: "long", year: "numeric" }) :
            view === "week" ? `${rangeStart.toDateString()} — ${rangeEnd.toDateString()}` :
            cursor.toDateString()}
        </div>
        <Button variant="outline" size="icon" onClick={() => shift(1)}><ChevronRight className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={()=>setCursor(new Date())}>Today</Button>
      </div>

      <Card className="p-4 rounded-2xl border-slate-200 dark:border-slate-800">
        <div className={`grid ${view === "day" ? "grid-cols-1" : view === "week" ? "grid-cols-7" : "grid-cols-7"} gap-2`}>
          {view !== "day" && ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
            <div key={d} className="text-[10px] uppercase tracking-widest text-slate-500 text-center py-1">{d}</div>
          ))}
          {days.map((d) => {
            const key = fmtDate(d);
            const dayItems = itemsByDay[key] || [];
            const isMonth = view === "month";
            const dim = isMonth && d.getMonth() !== cursor.getMonth();
            return (
              <div key={key + view} className={`rounded-xl border ${dim ? "opacity-40" : ""} border-slate-200 dark:border-slate-800 p-2 min-h-[110px] bg-white dark:bg-slate-900/40`}>
                <div className="text-xs font-mono text-slate-500 flex items-center justify-between">
                  <span>{d.getDate()}</span>
                  {dayItems.length > 0 && <span className="text-amber-600">{dayItems.length}</span>}
                </div>
                <div className="mt-1 space-y-1">
                  {dayItems.slice(0, view === "day" ? 20 : 4).map(a => (
                    <div key={a.id} className={`text-[11px] px-2 py-1 rounded-md border ${STATUS_COLORS[a.status] || STATUS_COLORS.scheduled}`} data-testid={`appt-${a.id}`}>
                      <div className="font-medium truncate">{a.client_name || "Client"}</div>
                      <div className="flex items-center gap-1 opacity-80"><Clock className="w-2.5 h-2.5" /> {(a.start || "").slice(11,16)} • {a.mode}</div>
                      {view === "day" && (
                        <Select value={a.status} onValueChange={v => updateStatus(a.id, v)}>
                          <SelectTrigger className="h-6 text-[10px] mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{["scheduled","checked-in","completed","no-show","cancelled"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>);
